import styled from '@emotion/styled';
import moment from 'moment';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navigationService, userAuthenticator } from 'src/config/rootService';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { Button, ButtonGroup } from 'src/shared/ds';
import { Input, InputType } from 'src/shared/ds/components/Input';
import { isEmailValid } from 'src/shared/helpers/emailValidator';
import { checkNameValidity } from 'src/shared/helpers/isNameValid';
import { color, font } from 'src/shared/styles/variables';

import { COMPANY_NOT_FOUND, EMAIL_IS_USED, IS_INELIGIBLE } from '../constants';

const Container = styled.div({
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
});
const ContentContainer = styled.div({
    textAlign: 'left',
    minWidth: '516px',
    flexDirection: 'column',
});
const Title = styled.h1(
    {
        paddingTop: '16px',
        color: color.ebonyClay,
        lineHeight: '44px',
        marginBottom: '8px',
    },
    font.heavy(font.size.headerPlus),
);
const Subtitle = styled.h3(
    {
        color: color.blueBayoux,
        lineHeight: '24px',
        paddingBottom: '10px',
        marginBottom: 0,
    },
    font.book(font.size.medium),
);
const ButtonsContainer = styled.div({ width: '100%', marginTop: 40, marginBottom: '16px' });
const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});
const InputContainer = styled.div({
    width: '100%',
    '& > *': {
        paddingTop: '16px',
    },
});
const SSOButton = styled.div({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
});

export enum BasicsRegistrationFormTestId {
    FirstNameField = 'RegistrationBasicsFormTestId.FirstNameField',
    LastNameField = 'RegistrationBasicsFormTestId.LastNameField',
    DateField = 'RegistrationBasicsFormTestId.DateField',
    Company = 'RegistrationBasicsFormTestId.Company',
    EmailField = 'RegistrationBasicsFormTestId.EmailField',
    SSOButton = 'RegistrationBasicsFormTestId.SSOButton',
}

export const RegistrationBasicsForm: React.FC = () => {
    const params = queryString.parse(window.location.search);
    const { t } = useTranslation();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [birthDateError, setBirthDateError] = useState('');
    const [email, setEmail] = useState('');
    const [companyCode, setCompanyCode] = useState(params?.company_code?.toString() || '');
    const [emailTaken, setEmailTaken] = useState(false);
    const [validCode, setValidCode] = useState(true);
    const [firstNameEdited, setWasFirstNameEdited] = useState(false);
    const [lastNameEdited, setWasLastNameEdited] = useState(false);
    const [birthDateEdited, setWasBirthDateEdited] = useState(false);
    const [companyEdited, setWasCompanyEdited] = useState(false);
    const [emailEdited, setWasEmailEdited] = useState(false);

    const data = {
        firstName,
        lastName,
        birthDate,
        companyCode,
        email,
    };

    const {
        isValid: isFirstNameValid,
        invalidCharacters: firstNameInvalidCharacters,
    } = checkNameValidity(firstName);

    const {
        isValid: isLastNameValid,
        invalidCharacters: lastNameInvalidCharacters,
    } = checkNameValidity(lastName);

    const onBlurFirstName = () => {
        setWasFirstNameEdited(true);
    };

    const onBlurLastName = () => {
        setWasLastNameEdited(true);
    };

    const onBlurBirthDate = () => {
        setWasBirthDateEdited(true);
    };

    const onBlurCompany = () => {
        setWasCompanyEdited(true);
    };

    const onBlurEmail = () => {
        setWasEmailEdited(true);
    };

    const defineFirstNameErrors = () => {
        let error = '';
        if (firstNameEdited && !firstName) {
            error = t('validation:first_name.required');
        }
        if (!isFirstNameValid) {
            error = t('auth:registration_form.errors.invalidName', {
                invalidCharacters: firstNameInvalidCharacters.join(', '),
            });
        }
        return error;
    };

    const defineLastNameErrors = () => {
        let error = '';
        if (lastNameEdited && !lastName) {
            error = t('validation:last_name.required');
        }
        if (!isLastNameValid) {
            error = t('auth:registration_form.errors.invalidName', {
                invalidCharacters: lastNameInvalidCharacters.join(', '),
            });
        }
        return error;
    };

    const defineEmailErrors = () => {
        let error = '';
        if (emailEdited && !email) {
            error = t('auth:registration_form.errors.emailMissing');
        }
        if (emailTaken) {
            error = t('auth:registration_form.errors.emailTaken');
        }
        if (emailEdited && email && !isEmailValid(email)) {
            error = t('auth:registration_form.errors.emailInvalid');
        }
        return error;
    };

    const defineCompanyErrors = () => {
        let error = '';
        if (companyEdited && !companyCode) {
            error = t('auth:registration_form.errors.companyMissing');
        }
        if (!validCode) {
            error =
                t('auth:registration_form.errors.companyNotFound') + ' help@joinmodernhealth.com';
        }
        return error;
    };

    const isFormValid = () => {
        return (
            firstName &&
            lastName &&
            companyCode &&
            email &&
            checkNameValidity(firstName.trim()).isValid &&
            checkNameValidity(lastName.trim()).isValid &&
            isEmailValid(email) &&
            birthDate &&
            is18OrOlder()
        );
    };

    const navigateToNextView = () => {
        navigationService.navigate('/registration', data);
    };

    const handleContinue = async () => {
        setEmailTaken(false);
        setValidCode(true);
        try {
            await userAuthenticator.validateEmailAndCompany(email, companyCode);
            await userAuthenticator.checkEligibility(data);
            navigateToNextView();
        } catch (errors) {
            if (errors.includes(EMAIL_IS_USED)) {
                setEmailTaken(true);
            }
            if (errors.includes(COMPANY_NOT_FOUND)) {
                setValidCode(false);
            }
            if (errors.includes(IS_INELIGIBLE)) {
                navigationService.navigate('/ineligible');
            }
        }
    };

    const is18OrOlder = () => {
        if (!birthDate) {
            return true;
        }

        return moment().diff(moment(birthDate, 'YYYY-MM-DD'), 'years') >= 18;
    };

    useEffect(() => {
        if (!is18OrOlder()) {
            setBirthDateError(t('auth:registration_form.errors.underaged'));
        } else if (birthDateEdited && !birthDate) {
            setBirthDateError(t('auth:registration_form.errors.missing_dob'));
        } else {
            setBirthDateError('');
        }
    }, [birthDate]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <ContentContainer>
                    <Title>{t('auth:registration_basics.title')}</Title>
                    <Subtitle>{t('auth:registration_basics.description')}</Subtitle>

                    <InputContainer>
                        <Input
                            required
                            fieldId="firstName"
                            testID={BasicsRegistrationFormTestId.FirstNameField}
                            type={InputType.TextField}
                            value={firstName}
                            label={t('auth:registration_form.labels.firstName')}
                            onChange={value => {
                                setFirstName(value.toString());
                            }}
                            onBlur={onBlurFirstName}
                            error={defineFirstNameErrors()}
                        />
                        <Input
                            required
                            fieldId="lastName"
                            testID={BasicsRegistrationFormTestId.LastNameField}
                            value={lastName}
                            type={InputType.TextField}
                            label={t('auth:registration_form.labels.lastName')}
                            onChange={value => {
                                setLastName(value.toString());
                            }}
                            onBlur={onBlurLastName}
                            error={defineLastNameErrors()}
                        />
                        <Input
                            fieldId="date"
                            testID={BasicsRegistrationFormTestId.DateField}
                            type={InputType.DatePicker}
                            value={birthDate ?? ''}
                            label={t('auth:registration_form.labels.dateOfBirth')}
                            onChange={value => setBirthDate(value.toString())}
                            onBlur={onBlurBirthDate}
                            error={birthDateError}
                        />
                        <Input
                            required
                            fieldId="company"
                            testID={BasicsRegistrationFormTestId.Company}
                            value={companyCode}
                            type={InputType.TextField}
                            label={t('auth:common_form_inputs.labels.company_code')}
                            onChange={value => {
                                setCompanyCode(value.toString());
                            }}
                            onBlur={onBlurCompany}
                            error={defineCompanyErrors()}
                        />
                        <Input
                            required
                            fieldId="email"
                            testID={BasicsRegistrationFormTestId.EmailField}
                            value={email}
                            type={InputType.TextField}
                            label={t('auth:registration_form.labels.email')}
                            onChange={value => {
                                setEmail(value.toString());
                            }}
                            onBlur={onBlurEmail}
                            error={defineEmailErrors()}
                        />
                    </InputContainer>
                    <ButtonsContainer>
                        <ButtonGroup
                            firstButtonText={t('auth:registration_page.back')}
                            secondButtonText={t('auth:login_form.continue')}
                            onFirstButtonClick={() => navigationService.navigate('/')}
                            onSecondButtonClick={handleContinue}
                            secondButtonDisabled={!isFormValid()}
                        />
                    </ButtonsContainer>
                    <SSOButton>
                        <Button
                            onClick={() =>
                                navigationService.navigate('/single-sign-on', {
                                    fromPath: 'revamp-signup',
                                })
                            }
                            title={t('auth:registration_page.button.sign_up_with_single_sign_on')}
                            variant="flat"
                            testID={BasicsRegistrationFormTestId.SSOButton}
                            size="large"
                        />
                    </SSOButton>
                </ContentContainer>
            </Container>
        </>
    );
};
